import React, { useCallback, useEffect, useState, useRef } from "react";
import { array, bool, func, object } from "prop-types";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { useInView } from "react-intersection-observer";
import { StoryElementCard } from "../../../arrow/components/Molecules/StoryElementCard";
import { StoryTags } from "../../../arrow/components/Atoms/StoryTags";
import { Paywall } from "../../../arrow/components/Rows/Paywall";
import AdComponent from "../../molecules/AdComponent";
import { useSummaryCheck } from "../../customHooks/useSummaryCheck";
import MoreAboutAuthors from "../../../arrow/components/Atoms/MoreAboutAuthors";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import FaqCard from "../../molecules/FaqCard";
import "./story-elements.m.css";
import SummaryCard from "../../molecules/SummaryCard";

const StoryCardsContent = React.memo(
  ({ story, cards, recommendedStories, isStoryBehindPaywall, renderAdComponent }) => {
    if (isStoryBehindPaywall) {
      return (
        <>
          <StoryElementCard
            story={story}
            card={cards[0]}
            key={get(cards[0], ["id"], "")}
            recommendedStories={recommendedStories}
            adComponent={renderAdComponent}
          />
          <Paywall magazine={true} />
        </>
      );
    }

    return cards.map((card, index) => {
      return (
        <div key={index}>
          <StoryElementCard
            card={card}
            story={story}
            recommendedStories={recommendedStories}
            adComponent={renderAdComponent}
          />
        </div>
      );
    });
  }
);
StoryCardsContent.displayName = "StoryCardsContent";
StoryCardsContent.propTypes = {
  story: object,
  cards: array,
  recommendedStories: array,
  isStoryBehindPaywall: bool,
  renderAdComponent: func,
};

const RenderCards = ({ story, cards, recommendedStories, taboola }) => {
  const qtState = useSelector((state) => state);
  const qtConfig = get(qtState, ["qt", "config"], {});
  // const isStoryBehindPaywall = !member && isMagazineStory;
  const isStoryBehindPaywall = false;
  const isDesktop = useDeviceType() === "desktop";
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";
  const isTexteStory = story["story-template"] === "text";

  const faqUrl = get(qtState, ["qt", "config", "publisher-attributes", "publisher-faq-url"], false);

  const [isAiOpen, setIsAiOpen] = useState(true);
  const [faqData, setFaqData] = useState([]);
  const targetRef = useRef(null);
  const [isTop, setIsTop] = useState(false);
  const [isFaqInViewport, setIsFaqInViewport] = useState(false);
  const storyId = get(story, ["id"], "") || get(story, ["story-content-id"], "");
  const { summaryPresent, showSummary, setShowSummary, summaryIndex } = useSummaryCheck(story.cards);
  const hasFaqData = faqData.length > 0;
  const [isfaqPopup, setIsfaqPopup] = useState(false);

  const handleSummaryClick = () => {
    trackStoryInteraction("floatingicon_click", "summary", () => setShowSummary(true));
  };

  const handleAiClick = () => {
    setIsAiOpen(!isAiOpen);
  };

  useEffect(() => {
    if (!isTexteStory || !storyId || faqUrl === false) {
      setFaqData([]);
      return;
    }

    const abortController = new AbortController();

    const fetchFaqData = async () => {
      try {
        const response = await fetch(`${faqUrl}/${encodeURIComponent(storyId)}`, { signal: abortController.signal });
        if (!response.ok) {
          throw new Error(`FAQ API failed with status ${response.status}`);
        }

        const data = await response.json();
        const hasFaqs = get(data, ["hasFaqs"], false);

        if (!hasFaqs) {
          setFaqData([]);
          return;
        }

        const apiFaqs = get(data, ["faqs"], []);
        const formattedFaqs = apiFaqs
          .map((faq) => ({
            title: get(faq, ["question"], ""),
            content: get(faq, ["answer"], ""),
          }))
          .filter((faq) => faq.title && faq.content);

        setFaqData(formattedFaqs);
      } catch (error) {
        if (error.name !== "AbortError") {
          setFaqData([]);
          console.error("Failed to fetch FAQs:", error);
        }
      }
    };
    fetchFaqData();

    return () => {
      abortController.abort();
    };
  }, [isTexteStory, storyId, faqUrl]);

  const pushInteractionEvent = ({ eventName, eventAction, linkText, callback }) => {
    const pageType = "story-page";
    const { category, subCategory } = getCategoryAndSubCategory(window.location, pageType, qtConfig, story);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      event_action: eventAction,
      ...(linkText ? { link_text: linkText } : {}),
      category,
      sub_category: subCategory,
      // content_id: contentId,
      template_name: getTemplateName(story?.["story-template"] || pageType),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
    if (typeof callback === "function") {
      callback();
    }
  };
  const trackStoryInteraction = (eventAction, linkText, callback) => {
    pushInteractionEvent({ eventName: "faq_interactions", eventAction, linkText, callback });
  };
  const trackFaqInteraction = (eventAction, linkText, callback) => {
    pushInteractionEvent({ eventName: "faq_interactions", eventAction, linkText, callback });
  };

  const handleFaqClick = () => {
    trackFaqInteraction("floatingicon_click", "faq", () => setIsfaqPopup(true));
  };
  const handleFaqOverlayClose = () => {
    trackFaqInteraction("overlay_close", "close", () => setIsfaqPopup(false));
  };
  const handleFaqInteraction = (eventAction, linkText) => {
    trackFaqInteraction(eventAction, linkText);
  };
  useEffect(() => {
    document.body.style.overflow = showSummary || isfaqPopup ? "hidden" : "unset";
  }, [showSummary, isfaqPopup]);

  useEffect(() => {
    const handleScroll = () => {
      if (targetRef.current) {
        const top = targetRef.current.getBoundingClientRect().top;
        const isMobile = window.innerWidth <= 768;
        const offset = isMobile ? 570 : 0;

        if (top <= offset) {
          setIsTop(true);
        } else {
          setIsTop(false);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const renderAdComponent = useCallback(
    (props) => <AdComponent {...props} recommendedStories={recommendedStories} isDesktop={isDesktop} story={story} />,
    [recommendedStories, isDesktop, story]
  );

  return (
    <div styleName="cards-wrapper" ref={targetRef}>
      {isTexteStory && hasFaqData && !isFaqInViewport && (
        <>
          <div styleName={`sticky-left-bar-outer ${isTop ? "active" : ""} ${isAdfreePage ? "adfree-space" : ""}`}>
            <div styleName={isAiOpen ? "sticky-left-bar" : "sticky-left-bar hidden"}>
              {hasFaqData && (
                <button aria-label="Open FAQs" onClick={handleFaqClick} styleName="questionsicon">
                  FAQs
                </button>
              )}
              {summaryPresent && (
                <button aria-label="Open Summary" onClick={handleSummaryClick} styleName="summaryicon">
                  Summarise
                </button>
              )}
            </div>
            <button
              aria-label="Ai-assistant-icon"
              styleName={isAiOpen ? "aiclose-icon active" : "aiclose-icon"}
              onClick={handleAiClick}
            ></button>
          </div>
          {summaryPresent && showSummary && (
            <SummaryCard setShowSummary={setShowSummary} story={story} summaryIndex={summaryIndex} />
          )}
          {showSummary && summaryPresent && <div styleName="blur-bg" />}
        </>
      )}
      <StoryCardsContent
        story={story}
        cards={cards}
        recommendedStories={recommendedStories}
        isStoryBehindPaywall={isStoryBehindPaywall}
        renderAdComponent={renderAdComponent}
      />
      {hasFaqData && isfaqPopup ? (
        <>
          {isfaqPopup && <div styleName="blur-bg" onClick={handleFaqOverlayClose} />}
          <div styleName={`faq-popup ${isfaqPopup ? "active" : ""}`}>
            <div styleName="popup-header-logo">
              <i styleName="fortune-faq-logo" />
              <button
                styleName="popup-close-button"
                onClick={() => setIsfaqPopup(false)}
                aria-label="Close FAQ"
              ></button>
            </div>
            <div styleName="faq-popup-inner">
              <FaqCard
                story={story}
                faqData={faqData}
                isfaqPopup={isfaqPopup}
                setIsfaqPopup={setIsfaqPopup}
                onFaqInteraction={handleFaqInteraction}
              />
            </div>
          </div>
        </>
      ) : (
        ""
      )}
      {hasFaqData && (
        <FaqCard
          story={story}
          faqData={faqData}
          isNoscroll={true}
          onFaqVisibilityChange={setIsFaqInViewport}
          onFaqInteraction={handleFaqInteraction}
        />
      )}
      {!isAdfreePage && isStoryBehindPaywall ? null : (
        <>
          <StoryTags tags={story.tags} template="noBorder" story={story} />
          <MoreAboutAuthors authors={story.authors} story={story} />
          {taboola && (
            <>
              <div id="taboola-below-article-thumbnails" className="TABOOLA"></div>
              <div
                dangerouslySetInnerHTML={{
                  __html: `
                      <script type="text/javascript">
                        window._taboola = window._taboola || [];
                        _taboola.push({
                          mode: 'alternating-thumbnails-a',
                          container: 'taboola-below-article-thumbnails',
                          placement: 'Below Article Thumbnails',
                          target_type: 'mix'
                        });
                      </script>
                  `,
                }}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

RenderCards.propTypes = {
  story: object,
  cards: array,
  recommendedStories: array,
  taboola: bool,
};

const StoryElements = ({
  story,
  cards,
  recommendedStories,
  config,
  isStickyBookReview = false,
  marginLeft = false,
}) => {
  const taboola = get(config, ["publisher-attributes", "taboola"], true);
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "300px 0px", // top margin of 300px
    threshold: 0.01, // when 10% is in view
  });

  useEffect(() => {
    if (inView && taboola === true) {
      // Initialize _taboola array if not already
      window._taboola = window._taboola || [];
      window._taboola.push({ article: "auto" });

      // Only insert script if not already present
      if (!document.getElementById("tb_loader_script")) {
        const s = document.createElement("script");
        s.async = true;
        s.src = "//cdn.taboola.com/libtrc/jambojar-fortuneindia/loader.js";
        s.id = "tb_loader_script";

        const f = document.getElementsByTagName("script")[0];
        f.parentNode.insertBefore(s, f);
      }

      // Optional: performance mark
      if (window.performance && typeof window.performance.mark === "function") {
        window.performance.mark("tbl_ic");
      }
    }
  }, [inView]);

  return (
    <div
      id="story-wrapper"
      styleName={`story-elements-wrapper ${isStickyBookReview ? "book-review-elements-wrapper" : ""} ${
        isStickyBookReview ? "review-elements-wrapper" : ""
      } ${marginLeft ? "marginLeft" : ""}`}
      ref={ref}
    >
      <RenderCards story={story} cards={cards} recommendedStories={recommendedStories} taboola={taboola} />
    </div>
  );
};

StoryElements.propTypes = {
  story: object,
  cards: array,
  recommendedStories: array,
  config: object,
  isStickyBookReview: bool,
  marginLeft: bool,
};

export default StoryElements;
